import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Heart, Smile, Meh, Frown, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const DashboardCheckin = () => {
  const [loading, setLoading] = useState(true);
  const [todayCheckin, setTodayCheckin] = useState<any>(null);
  const [mood, setMood] = useState<string>("");
  const [energyLevel, setEnergyLevel] = useState<number>(3);
  const [notes, setNotes] = useState("");
  const [goalsForDay, setGoalsForDay] = useState("");

  useEffect(() => {
    loadTodayCheckin();
  }, []);

  const loadTodayCheckin = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const today = new Date().toISOString().split('T')[0];

    const { data, error } = await supabase
      .from("daily_checkins")
      .select("*")
      .eq("user_id", user.id)
      .eq("checkin_date", today)
      .maybeSingle();

    if (error) {
      console.error("Error loading check-in:", error);
    } else if (data) {
      setTodayCheckin(data);
      setMood(data.mood || "");
      setEnergyLevel(data.energy_level || 3);
      setNotes(data.notes || "");
      setGoalsForDay(data.goals_for_day || "");
    }
    setLoading(false);
  };

  const handleSave = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const today = new Date().toISOString().split('T')[0];

    const checkinData = {
      user_id: user.id,
      checkin_date: today,
      mood,
      energy_level: energyLevel,
      notes,
      goals_for_day: goalsForDay,
    };

    const { error } = await supabase
      .from("daily_checkins")
      .upsert(checkinData);

    if (error) {
      toast.error("Failed to save check-in");
      console.error(error);
    } else {
      toast.success("Daily check-in saved!");
      loadTodayCheckin();
    }
  };

  const moods = [
    { value: "great", label: "Great", icon: Smile, color: "text-green-500" },
    { value: "good", label: "Good", icon: Smile, color: "text-blue-500" },
    { value: "okay", label: "Okay", icon: Meh, color: "text-yellow-500" },
    { value: "struggling", label: "Struggling", icon: Frown, color: "text-red-500" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Link to="/dashboard">
        <Button variant="ghost" className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <Heart className="w-8 h-8 text-primary" />
        <h1 className="text-3xl font-bold">Daily Check-in</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>How are you feeling today?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label>Mood</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              {moods.map((moodOption) => {
                const Icon = moodOption.icon;
                return (
                  <button
                    key={moodOption.value}
                    onClick={() => setMood(moodOption.value)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      mood === moodOption.value
                        ? "border-primary bg-primary/10"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Icon className={`w-8 h-8 mx-auto mb-2 ${moodOption.color}`} />
                    <p className="text-sm font-medium">{moodOption.label}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Energy Level: {energyLevel}/5</Label>
            <input
              type="range"
              min="1"
              max="5"
              value={energyLevel}
              onChange={(e) => setEnergyLevel(parseInt(e.target.value))}
              className="w-full mt-4"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>Low</span>
              <span>High</span>
            </div>
          </div>

          <div>
            <Label htmlFor="goals">Goals for Today</Label>
            <Textarea
              id="goals"
              value={goalsForDay}
              onChange={(e) => setGoalsForDay(e.target.value)}
              placeholder="What do you want to accomplish today?"
              rows={3}
              className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional thoughts or reflections..."
              rows={4}
              className="mt-2"
            />
          </div>

          <Button onClick={handleSave} className="w-full">
            {todayCheckin ? "Update Check-in" : "Save Check-in"}
          </Button>

          {todayCheckin && (
            <p className="text-sm text-muted-foreground text-center">
              Last updated: {new Date(todayCheckin.created_at).toLocaleTimeString()}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardCheckin;
